import { isContentClean, getFilterMessage } from '../utils/contentFilter';
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  Box,
  Spinner,
  useToast,
  Avatar,
} from '@chakra-ui/react';
import { Send, ArrowLeft } from 'lucide-react';
import { auth } from '../services/firebase';
import {
  Message,
  getOrCreateConversation,
  getConversationId,
  getConversation,
  sendMessage,
  subscribeToMessages,
  markMessagesAsRead,
  canMessage,
} from '../services/messageService';
import userService from '../services/userService';
import { POKER_COLORS } from '../theme/designSystem';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  // MessagesPage passes these names:
  userId: string;
  username: string;
  photoURL?: string;
  // Callback to navigate to Feed tab
  onNavigateToFeed?: (storyId?: string) => void;
}

const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  userId: targetUserId,
  username: targetUsername,
  photoURL: targetPhotoURL,
  onNavigateToFeed,
}) => {
  const currentUserId = auth.currentUser?.uid;
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [canChat, setCanChat] = useState<boolean | null>(null);

  // Check if user can message and set up conversation
  useEffect(() => {
    if (!isOpen || !currentUserId || !targetUserId) return;

    const initChat = async () => {
      setLoading(true);
      console.log('[ChatModal] Starting initChat...', { currentUserId, targetUserId });
      
      try {
        // First check if conversation already exists
        const existingConvId = getConversationId(currentUserId, targetUserId);
        console.log('[ChatModal] Checking for existing conversation:', existingConvId);
        
        const existingConv = await getConversation(existingConvId);
        console.log('[ChatModal] Existing conversation result:', existingConv ? 'FOUND' : 'NOT FOUND');
        
        if (existingConv) {
          // Conversation exists - allow viewing
          console.log('[ChatModal] Using existing conversation');
          setConversationId(existingConvId);
          setCanChat(true);
          await markMessagesAsRead(existingConvId, currentUserId);
          setLoading(false);
          return;
        }

        // No existing conversation - check if they're friends before creating
        console.log('[ChatModal] No existing conversation, checking friendship...');
        const allowed = await canMessage(currentUserId, targetUserId);
        console.log('[ChatModal] canMessage result:', allowed);
        setCanChat(allowed);

        if (!allowed) {
          setLoading(false);
          return;
        }

        // Get or create conversation
        console.log('[ChatModal] Creating new conversation...');
        const convId = await getOrCreateConversation(currentUserId, targetUserId);
        setConversationId(convId);
        await markMessagesAsRead(convId, currentUserId);
        console.log('[ChatModal] Done');
      } catch (error) {
        console.error('[ChatModal] Error initializing chat:', error);
        toast({
          title: 'Error',
          description: 'Failed to load chat',
          status: 'error',
          duration: 3000,
        });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    initChat();
  }, [isOpen, currentUserId, targetUserId, toast]);

  // Subscribe to messages
  useEffect(() => {
    if (!conversationId || !currentUserId) return;

    const unsubscribe = subscribeToMessages(conversationId, (msgs) => {
      setMessages(msgs);
      // Mark as read when new messages arrive
      markMessagesAsRead(conversationId, currentUserId);
    });

    return () => unsubscribe();
  }, [conversationId, currentUserId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setNewMessage('');
      setConversationId(null);
      setCanChat(null);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!newMessage.trim() || !conversationId || !currentUserId || sending) return;
    if (!isContentClean(newMessage.trim())) {
      toast({ title: 'Message not sent', description: getFilterMessage('chat'), status: 'error', duration: 4000 });
      return;
    }

    setSending(true);
    try {
      await sendMessage(conversationId, currentUserId, newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups: { [key: string]: Message[] }, message) => {
    const dateKey = message.createdAt ? formatDate(message.createdAt) : 'Unknown';
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
    return groups;
  }, {});

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent 
        bg="#1C1C1E" 
        border="1px solid"
        borderColor="gray.800"
        maxH="80vh"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <ModalHeader 
          borderBottom="1px solid" 
          borderColor="gray.800"
          py={3}
        >
          <HStack spacing={3}>
            <IconButton
              aria-label="Close"
              icon={<ArrowLeft size={20} />}
              variant="ghost"
              color="gray.400"
              size="sm"
              onClick={onClose}
              _hover={{ bg: 'gray.800' }}
            />
            <Avatar
              size="sm"
              name={targetUsername}
              src={targetPhotoURL}
              bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
            />
            <VStack align="start" spacing={0}>
              <Text color="white" fontWeight="semibold" fontSize="md">
                {targetUsername}
              </Text>
              <Text color="gray.500" fontSize="xs">
                Direct Message
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        {/* Body */}
        <ModalBody 
          flex={1} 
          display="flex" 
          flexDirection="column" 
          p={0}
          overflow="hidden"
        >
          {loading ? (
            <VStack flex={1} justify="center" py={10}>
              <Spinner color="orange.400" size="lg" />
              <Text color="gray.500" fontSize="sm">Loading chat...</Text>
            </VStack>
          ) : canChat === false ? (
            <VStack flex={1} justify="center" py={10} px={4}>
              <Text color="gray.400" fontSize="lg" textAlign="center">
                You can only message friends
              </Text>
              <Text color="gray.500" fontSize="sm" textAlign="center">
                You and {targetUsername} must follow each other to chat.
              </Text>
            </VStack>
          ) : (
            <>
              {/* Messages Area */}
              <Box 
                flex={1} 
                overflowY="auto" 
                px={4} 
                py={3}
                css={{
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '3px' },
                }}
              >
                {messages.length === 0 ? (
                  <VStack py={10}>
                    <Text color="gray.500" fontSize="sm">
                      No messages yet
                    </Text>
                    <Text color="gray.600" fontSize="xs">
                      Say hi to {targetUsername}! 

                    </Text>
                  </VStack>
                ) : (
                  Object.entries(groupedMessages).map(([date, dateMessages]) => (
                    <Box key={date}>
                      {/* Date Divider */}
                      <HStack justify="center" my={4}>
                        <Box h="1px" flex={1} bg="gray.800" />
                        <Text color="gray.500" fontSize="xs" px={3}>
                          {date}
                        </Text>
                        <Box h="1px" flex={1} bg="gray.800" />
                      </HStack>

                      {/* Messages for this date */}
                      {dateMessages.map((message) => {
                        const isOwn = message.senderId === currentUserId;
                        return (
                          <HStack
                            key={message.id}
                            justify={isOwn ? 'flex-end' : 'flex-start'}
                            mb={2}
                          >
                            <Box
                              maxW="75%"
                              bg={isOwn 
                                ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' 
                                : (message as any).type?.includes('mention') 
                                  ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)'
                                  : 'gray.800'
                              }
                              color="white"
                              px={4}
                              py={2}
                              borderRadius="2xl"
                              borderBottomRightRadius={isOwn ? 'sm' : '2xl'}
                              borderBottomLeftRadius={isOwn ? '2xl' : 'sm'}
                              cursor={(message as any).storyId ? 'pointer' : 'default'}
                              onClick={() => {
                                const storyId = (message as any).storyId;
                                if (storyId) {
                                  onClose();
                                  if (onNavigateToFeed) {
                                    onNavigateToFeed(storyId);
                                  } else {
                                    // Fallback: just close modal, user is already on messages page
                                    // They can manually click Feed tab
                                  }
                                }
                              }}
                              _hover={(message as any).storyId ? { opacity: 0.9, transform: 'scale(1.01)' } : {}}
                              transition="all 0.2s"
                            >
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {message.text}
                              </Text>
                              {(message as any).storyId && (
                                <Text fontSize="2xs" color="whiteAlpha.800" mt={1}>
                                   Tap to view in Feed

                                </Text>
                              )}
                              <Text 
                                fontSize="2xs" 
                                color={isOwn ? 'whiteAlpha.700' : 'gray.500'}
                                textAlign="right"
                                mt={1}
                              >
                                {formatTime(message.createdAt)}
                                {isOwn && message.read && ' '}

                              </Text>
                            </Box>
                          </HStack>
                        );
                      })}
                    </Box>
                  ))
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box 
                p={3} 
                borderTop="1px solid" 
                borderColor="gray.800"
                bg="#1C1C1E"
              >
                <HStack spacing={2}>
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    bg="gray.900"
                    border="1px solid"
                    borderColor="gray.700"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{ borderColor: 'orange.400' }}
                    borderRadius="full"
                    size="md"
                  />
                  <IconButton
                    aria-label="Send message"
                    icon={<Send size={18} />}
                    bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                    color="white"
                    borderRadius="full"
                    size="md"
                    onClick={handleSend}
                    isLoading={sending}
                    isDisabled={!newMessage.trim()}
                    _hover={{ transform: 'scale(1.05)' }}
                    _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                </HStack>
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChatModal;